<template>
    <div class="bill-applicable-discounts-list">
        <ScrollableView class="discounts-section" v-if="!isLoading && !isApplying && hasApplicableDiscounts">
            <section class="items-container">
                <ApplicableDiscountTile
                    v-for="item in applicableDiscounts"
                    :key="item.id"
                    :item="item"
                    :isAvailable="true"
                    @discountSelected="onClickApplyDiscount(item)"
                />
            </section>
        </ScrollableView>
        <div class="no-applicable-discounts-msg" v-if="!isLoading && !hasApplicableDiscounts">
            <span>{{ translations.discounts.noApplicableDiscountsFound }}</span>
        </div>
        <div class="discounts-loader-container" v-if="isLoading">
            <span>{{ translations.offerSection.loadingData }}</span>
        </div>
        <div class="discounts-loader-container" v-if="!isLoading && isApplying">
            <span>{{ translations.discounts.applyingDiscount }}</span>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { EnterDiscountOpenValuePopoverVue } from "@/app/popover/EnterDiscountOpenValuePopover";
    import { Popup } from "@/components/dialog/Popup";
    import { Factory } from "@/data/dao/Factory";
    import type { DaoError } from "@/data/dao/service/BaseServiceDao";
    import { CustomerDao } from "@/data/dao/service/CustomerDao";
    import type { IDiscount } from "@/data/model/Discount";
    import { DiscountAccessMode, DiscountType } from "@/data/model/Enums";
    import type { IBillDiscountAppliedEventData, ICartDiscountAppliedEventData } from "@/data/model/Voucher";
    import { trans } from "@/operator/localization/Trans";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { createEmptyDiv, createVueComponent } from "@/shared/utils/vue";
    import { computed, onMounted, ref } from "vue";

    import ApplicableDiscountTile from "./ApplicableDiscountTile.vue";
    import EnterDiscountOpenValueModal from "@/components/discount/EnterDiscountOpenValueModal.vue";
    import ScrollableView from "@/components/layout/ScrollableView.vue";
    import { DiscountsDao } from "@/data/dao/service/DiscountsDao";

    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);
    const discountDao = Factory.getInstance<DiscountsDao>(DiscountsDao);

    let enterDiscountOpenValuePopover: EnterDiscountOpenValuePopoverVue | null = null;
    // either cartId (cart mode) or billId + payUseCaseId (bill mode) have to be supplied
    const props = defineProps<{
        inOperatorApp: boolean;
        locationId: string;
        billId?: string;
        payUseCaseId?: string;
        selectedMainBillItemIds?: string[];
        cartId?: string;
        selectedMainCartItemIds?: string[];
    }>();

    const emit = defineEmits<{
        onClose: [];
        billDiscountApplied: [eventData: IBillDiscountAppliedEventData];
        cartDiscountApplied: [eventData: ICartDiscountAppliedEventData];
        applyDiscountErrorOccurred: [error: DaoError];
    }>();

    // define html refs
    const applicableDiscounts = ref<IDiscount[]>([]);
    const hasApplicableDiscounts = computed(() => applicableDiscounts.value.length > 0);
    const isLoading = ref<boolean>(false);
    const isApplying = ref<boolean>(false);

    async function loadApplicableDiscounts() {
        try {
            isLoading.value = true;

            const discounts = await discountDao.getApplicableDiscounts({
                locationId: props.locationId,
            });
            applicableDiscounts.value = discounts;
        } catch (ex) {
            logError(ex);
            Popup.show(
                {
                    containerClass: "bill-applicable-discounts-error-popup",
                    title: trans.general.error,
                    message: trans.general.operationFailed,
                },
                $("body")
            );
        } finally {
            isLoading.value = false;
        }
    }

    function openEnterDiscountOpenValuePopup(discountType: DiscountType, onEnterValue: (value: number) => void) {
        const result = createVueComponent(
            EnterDiscountOpenValueModal,
            {
                onEnterValue: onEnterValue,
                discountType: discountType,
            },
            createEmptyDiv(),
            true
        );
        const popup = result.mountedComponent;
        popup.show();
    }

    function openEnterDiscountOpenValuePopover(discountType: DiscountType, onEnterValue: (value: number) => void) {
        if (enterDiscountOpenValuePopover != null) {
            enterDiscountOpenValuePopover.hide();
            enterDiscountOpenValuePopover = null;
        }

        enterDiscountOpenValuePopover = new EnterDiscountOpenValuePopoverVue({
            onEnterValue: onEnterValue,
            discountType: discountType,
            uniqueId: "enter-discount-open-value",
        });
        enterDiscountOpenValuePopover.show();
    }

    async function onClickApplyDiscount(discount: IDiscount) {
        if (discount.isOpenValue) {
            if (props.inOperatorApp) {
                openEnterDiscountOpenValuePopup(discount.type, async (value: number) => {
                    await applyDiscount(discount, value);
                });
            } else {
                emit("onClose");
                openEnterDiscountOpenValuePopover(discount.type, async (value) => {
                    await applyDiscount(discount, value);
                });
            }
        } else {
            await applyDiscount(discount, undefined);
        }
    }

    async function applyDiscount(discount: IDiscount, openValue: number | undefined) {
        if (isApplying.value || isLoading.value) return;

        try {
            isApplying.value = true;

            if (props.cartId != null) {
                await applyDiscountToCart(discount, openValue);
            } else {
                await applyDiscountToBill(discount, openValue);
            }
        } catch (ex) {
            logError(ex);
            const error = ex as DaoError;
            emit("applyDiscountErrorOccurred", error);
        } finally {
            isApplying.value = false;
        }
    }

    async function applyDiscountToBill(discount: IDiscount, openValue: number | undefined) {
        const response = await customerDao.applyDiscountToBill({
            code: discount.code,
            billId: props.billId!,
            payUseCaseId: props.payUseCaseId!,
            discountAccessMode: DiscountAccessMode.Button,
            selectedMainBillItemIds: props.selectedMainBillItemIds,
            discountOpenValue: openValue,
        });

        if (response == null) {
            throw Error("No response from the server");
        }

        const eventData: IBillDiscountAppliedEventData = {
            bill: response,
        };
        emit("billDiscountApplied", eventData);
    }

    async function applyDiscountToCart(discount: IDiscount, openValue: number | undefined) {
        const response = await customerDao.applyDiscountToCart({
            code: discount.code,
            cartId: props.cartId!,
            discountAccessMode: DiscountAccessMode.Button,
            selectedMainCartItemIds: props.selectedMainCartItemIds,
            discountOpenValue: openValue,
        });

        if (response == null) {
            throw Error("No response from the server");
        }

        const eventData: ICartDiscountAppliedEventData = {
            cart: response,
        };
        emit("cartDiscountApplied", eventData);
    }

    onMounted(() => {
        loadApplicableDiscounts();
    });
</script>

<style lang="scss">
    .bill-applicable-discounts-list {
        min-height: 500px;
        min-height: 70vh;
        display: flex;
        justify-content: center;

        .discounts-section {
            flex: 1 1 auto;

            .items-container {
                flex: 1 1 auto;
                display: flex;
                flex-wrap: wrap;
                align-content: flex-start;
                justify-content: space-between;
                padding: 3%;

                .applicable-discount-tile {
                    flex: 0 0 48%;
                    margin-bottom: 3%;
                    max-width: 48%;
                    min-width: 85px;
                }
            }
        }

        .discounts-loader-container,
        .no-applicable-discounts-msg {
            padding-top: 5em;
            font-size: 1.1em;
        }
    }

    .bill-applicable-discounts-error-popup {
        z-index: 1510;
    }
</style>
